import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, CheckCircle, XCircle, Clock, Eye, Search, Filter, 
  MapPin, Mail, Phone, Globe, Calendar, User, Star, Bed,
  ChevronDown, RefreshCw, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', variant: 'outline', className: 'text-amber-600 border-amber-300 bg-amber-50' },
  APPROVED: { label: 'Approved', variant: 'outline', className: 'text-green-600 border-green-300 bg-green-50' },
  REJECTED: { label: 'Rejected', variant: 'outline', className: 'text-red-600 border-red-300 bg-red-50' },
};

const PROPERTY_TYPE_LABELS = {
  HOTEL: 'Hotel',
  RESORT: 'Resort',
  BOUTIQUE: 'Boutique Hotel',
  MOTEL: 'Motel',
  HOSTEL: 'Hostel',
  APARTMENT: 'Serviced Apartment',
  VILLA: 'Villa',
  BNB: 'Bed & Breakfast',
};

export default function RegistrationApprovalsPage() {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('hms_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/registrations/stats/summary`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'ALL' 
        ? `${API_URL}/api/registrations/all`
        : `${API_URL}/api/registrations/all?status_filter=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch registrations');
      }
      
      const data = await response.json();
      setRegistrations(data);
    } catch (error) {
      toast.error('Failed to load registrations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRegistrations();
    fetchStats();
  }, [fetchRegistrations]);

  const fetchRegistrationDetails = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/registrations/${id}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedRegistration(data);
        setDetailsDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load registration details');
    }
  };

  const handleApprove = async () => {
    if (!selectedRegistration) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registrations/${selectedRegistration.id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: actionNotes }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Approval failed');
      }
      
      toast.success(`${selectedRegistration.hotel_name} has been approved!`);
      setApproveDialogOpen(false);
      setDetailsDialogOpen(false);
      setActionNotes('');
      fetchRegistrations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/registrations/${selectedRegistration.id}/reject`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: actionNotes }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Rejection failed');
      }
      
      toast.success(`Registration for ${selectedRegistration.hotel_name} has been rejected.`);
      setRejectDialogOpen(false);
      setDetailsDialogOpen(false);
      setActionNotes('');
      fetchRegistrations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      reg.hotel_name.toLowerCase().includes(term) ||
      reg.hotel_code.toLowerCase().includes(term) ||
      reg.admin_email.toLowerCase().includes(term) ||
      reg.address_city.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6" data-testid="registration-approvals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Hotel Registrations</h1>
          <p className="text-muted-foreground">Review and manage hotel registration applications</p>
        </div>
        <Button variant="outline" onClick={() => { fetchRegistrations(); fetchStats(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Building2 className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by hotel name, code, email, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Registrations List */}
      <Card>
        <CardHeader>
          <CardTitle>Applications ({filteredRegistrations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No registrations found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRegistrations.map(reg => (
                <div 
                  key={reg.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => fetchRegistrationDetails(reg.id)}
                  data-testid={`registration-item-${reg.id}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-medium">{reg.hotel_name}</p>
                        <Badge variant="secondary" className="text-xs">{reg.hotel_code}</Badge>
                        {reg.star_rating && (
                          <span className="flex items-center text-xs text-amber-600">
                            {reg.star_rating}<Star className="w-3 h-3 ml-0.5 fill-amber-400" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center">
                          <MapPin className="w-3 h-3 mr-1" />
                          {reg.address_city}, {reg.address_country}
                        </span>
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {reg.admin_full_name}
                        </span>
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(reg.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge className={STATUS_CONFIG[reg.status]?.className}>
                      {STATUS_CONFIG[reg.status]?.label}
                    </Badge>
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>Registration Details</span>
              {selectedRegistration && (
                <Badge className={STATUS_CONFIG[selectedRegistration.status]?.className}>
                  {STATUS_CONFIG[selectedRegistration.status]?.label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              Application submitted on {selectedRegistration && formatDate(selectedRegistration.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedRegistration && (
            <ScrollArea className="flex-1 pr-4">
              <Tabs defaultValue="hotel" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="hotel">Hotel</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="admin">Administrator</TabsTrigger>
                </TabsList>
                
                <TabsContent value="hotel" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Hotel Name</Label>
                      <p className="font-medium">{selectedRegistration.hotel_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Hotel Code</Label>
                      <p className="font-medium">{selectedRegistration.hotel_code}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Property Type</Label>
                      <p className="font-medium">{PROPERTY_TYPE_LABELS[selectedRegistration.property_type] || selectedRegistration.property_type}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Star Rating</Label>
                      <p className="font-medium flex items-center">
                        {selectedRegistration.star_rating ? (
                          <>
                            {selectedRegistration.star_rating} <Star className="w-4 h-4 ml-1 fill-amber-400 text-amber-400" />
                          </>
                        ) : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Total Rooms</Label>
                      <p className="font-medium flex items-center">
                        {selectedRegistration.total_rooms ? (
                          <>
                            <Bed className="w-4 h-4 mr-1" />
                            {selectedRegistration.total_rooms} rooms
                          </>
                        ) : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Contact Email</Label>
                      <p className="font-medium flex items-center">
                        <Mail className="w-4 h-4 mr-1" />
                        {selectedRegistration.hotel_email}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Contact Phone</Label>
                      <p className="font-medium flex items-center">
                        {selectedRegistration.hotel_phone ? (
                          <>
                            <Phone className="w-4 h-4 mr-1" />
                            {selectedRegistration.hotel_phone}
                          </>
                        ) : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Website</Label>
                      <p className="font-medium flex items-center">
                        {selectedRegistration.website ? (
                          <>
                            <Globe className="w-4 h-4 mr-1" />
                            {selectedRegistration.website}
                          </>
                        ) : 'Not specified'}
                      </p>
                    </div>
                  </div>
                  {selectedRegistration.description && (
                    <div>
                      <Label className="text-muted-foreground">Description</Label>
                      <p className="text-sm mt-1">{selectedRegistration.description}</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="location" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-muted-foreground">Street Address</Label>
                      <p className="font-medium">{selectedRegistration.address_street || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">City</Label>
                      <p className="font-medium">{selectedRegistration.address_city}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">State/Province</Label>
                      <p className="font-medium">{selectedRegistration.address_state || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Country</Label>
                      <p className="font-medium">{selectedRegistration.address_country}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Timezone</Label>
                      <p className="font-medium">{selectedRegistration.timezone}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Currency</Label>
                      <p className="font-medium">{selectedRegistration.currency}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Legal Name</Label>
                      <p className="font-medium">{selectedRegistration.legal_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Tax ID</Label>
                      <p className="font-medium">{selectedRegistration.tax_id || 'Not specified'}</p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="admin" className="space-y-4 mt-4">
                  <div className="p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{selectedRegistration.admin_full_name}</p>
                        <p className="text-sm text-muted-foreground">Property Administrator</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Email</Label>
                        <p className="font-medium">{selectedRegistration.admin_email}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Phone</Label>
                        <p className="font-medium">{selectedRegistration.admin_phone || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                  {selectedRegistration.ip_address && (
                    <div className="text-sm text-muted-foreground">
                      Submitted from IP: {selectedRegistration.ip_address}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Rejection Reason (if rejected) */}
              {selectedRegistration.status === 'REJECTED' && selectedRegistration.rejection_reason && (
                <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50">
                  <p className="text-sm font-medium text-red-800 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Rejection Reason
                  </p>
                  <p className="text-sm text-red-700 mt-1">{selectedRegistration.rejection_reason}</p>
                </div>
              )}
            </ScrollArea>
          )}

          <DialogFooter className="mt-4">
            {selectedRegistration?.status === 'PENDING' && (
              <div className="flex space-x-2 w-full">
                <Button 
                  variant="destructive" 
                  className="flex-1"
                  onClick={() => setRejectDialogOpen(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => setApproveDialogOpen(true)}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </div>
            )}
            {selectedRegistration?.status !== 'PENDING' && (
              <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Registration</DialogTitle>
            <DialogDescription>
              This will create a new tenant, property, and admin user for {selectedRegistration?.hotel_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes for this approval..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApprove} 
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading ? 'Approving...' : 'Confirm Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Registration</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting the registration for {selectedRegistration?.hotel_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Rejection *</Label>
              <Textarea
                placeholder="Enter the reason for rejection..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReject} 
              disabled={actionLoading || !actionNotes.trim()}
            >
              {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
